/**
 * The three seams that make one renderer serve Edit, Preview and SCORM (CEE-896).
 *
 * See ./README.md. Everything except these three is identical
 * across the three modes:
 *
 *   Slot           Edit: outline + click-to-edit.   Preview/SCORM: identity, so no editor
 *                  code reaches the exported bundle.
 *   resolveAsset   Edit/Preview: signed CloudFront URL.  SCORM: `media/img_04.png`, relative.
 *   ProgressSink   Edit: no-op.  Preview: in-memory.  SCORM: writes `cmi.*`.
 *
 * No MUI and no Atlas imports in this directory: it is meant to lift out to
 * `packages/course-renderer` verbatim once the SCORM player shell exists.
 */
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { DEFAULT_CHROME_STRINGS, type ChromeStrings } from './chromeStrings';

export type RenderMode = 'edit' | 'preview' | 'scorm';

/** A path into the slide document, e.g. `zones.heading.text`, `interaction.items[2].body`. */
export type SlidePath = string;

export interface SlotProps {
  /** Same vocabulary as `InteractionField.path` in the interaction registry. */
  path: SlidePath;
  /** Human label for the edit affordance; unused outside edit mode. */
  label?: string;
  children: ReactNode;
}

/** Emitted by payload components. Deliberately free of any `cmi.` vocabulary. */
export interface AnsweredEvent {
  slideNumber: number;
  /** Layout id, so the sink can look up verb/interactionType on the family registry. */
  layout: string;
  /** Learner's response, already normalised by the component that owns the payload. */
  response: string;
  /**
   * Decided by the RENDERER, because it is the only thing holding the answer key
   * (`correctOptionId`, `items[].bucketId`). The sink does family-agnostic bookkeeping.
   */
  correct: boolean;
  /**
   * Whether this response SETTLES the question — right, or wrong with no retry left.
   *
   * A wrong answer with attempts remaining is still reported (it is a real interaction and it
   * belongs in the LMS's interaction log), but the question is not finished, so the sink must not
   * treat the slide as done. Only the renderer knows how many tries the board allows, which is why
   * the flag is emitted rather than inferred from `correct` (CEE-1220).
   */
  final: boolean;
}

export interface ProgressSink {
  onAnswered?: (event: AnsweredEvent) => void;
  /** Viewed a carousel panel, opened an accordion row, ticked a checklist item. */
  onInteracted?: (slideNumber: number, itemId: string) => void;
  /**
   * Module ids the learner has finished — drives the green check on `index_slide` cards.
   * Host-owned progress; never a field on the slide JSON (CEE-970).
   */
  completedModuleIds?: ReadonlySet<string> | readonly string[];
  /** Learner opened a module card from the index hub. */
  onModuleOpen?: (slideNumber: number, moduleId: string) => void;
  /** Learner clicked Finish course once every module is complete. */
  onFinishCourse?: () => void;
}

/**
 * Where interactive payloads report "the learner has finished me", so a host can hold Next.
 *
 * Declared with the other seam types rather than in `nextGate.tsx` to keep the import one-way:
 * the gate helpers read this context, this file must not read them back.
 */
export interface NextGate {
  /** Declare (or update) one requirement for leaving the current slide. */
  report: (requirementId: string, satisfied: boolean) => void;
  /** Withdraw a requirement — the payload that owned it is gone. */
  release: (requirementId: string) => void;
}

export interface MediaEditOverlayProps {
  /** JSON path to the image key on the slide document. */
  imagePath: string;
  imageKey: string | undefined;
}

/** Edit-mode bucket selector and feedback affordance on sorting tray cards (CEE-1153). */
export interface SortingTrayControlsProps {
  itemIndex: number;
  itemLabel: string;
  bucketId: string;
  buckets: ReadonlyArray<{ id: string; label: string }>;
  onBucketChange: (nextBucketId: string) => void;
  onEditFeedback: () => void;
  feedbackEditLabel: string;
}

export interface RenderContextValue {
  mode: RenderMode;
  /** Wraps every editable region. Identity outside edit mode. */
  Slot: (props: SlotProps) => ReactNode;
  /**
   * Edit-mode slide patches. Payload chrome (e.g. sorting bucket selects) uses the same path
   * vocabulary as `Slot` and `setSlideTextAtPath`.
   */
  onTextChange?: (path: SlidePath, text: string) => void;
  /** Edit-mode undo boundary; called when a field edit is committed (blur / select change). */
  onTextCommit?: (path: SlidePath, previousText: string, nextText: string) => void;
  /** imageKey -> src. Returns null when the key cannot be resolved yet. */
  resolveAsset: (imageKey: string | undefined) => string | null;
  /**
   * Whether the host is still resolving this deck's image keys (CEE-1211).
   *
   * `resolveAsset` returns null for "not fetched yet" and for "will never resolve" alike, so
   * without this a slide cannot tell a wait from a missing image and has to assume the worse of
   * the two. The editor resolves a whole deck's presigned URLs in one request, so this is one
   * deck-wide flag rather than per-key state.
   *
   * Three-valued on purpose — `undefined` is not the same as `false`:
   *   undefined  the host resolves synchronously and has no notion of a wait. A SCORM package
   *              reads its images out of the zip, so it passes nothing and gets today's
   *              behaviour exactly, label and all.
   *   true       resolution in flight; a null src is a wait, not a missing image.
   *   false      resolution settled; a null src really is unresolvable.
   */
  assetsPending?: boolean;
  progress: ProgressSink;
  /**
   * Advance to the next slide, for the Next control the board draws INSIDE some slides.
   *
   * A seam rather than a prop because the button is rendered several layouts deep, and a fourth
   * seam is cheaper than threading a callback through every layout. Absent in edit mode and in
   * the rail thumbnails, where the control is decoration; supplied by the deck player.
   */
  onNext?: () => void;
  /**
   * Label for that control, when 'Next' would be wrong.
   *
   * The one caller today is a composed CoC training (CEE-734): the last slide of a module goes
   * back to the index hub, not on to the next module. Where the control goes is the host's
   * policy, so the wording has to be too — but it is one string on the seam, not module knowledge
   * inside the renderer.
   */
  nextLabel?: string;
  /** Learner-facing chrome labels for the active language (Next, Submit, Welcome, …). */
  strings: ChromeStrings;
  /** Edit-mode overlay on the slide media column (e.g. Change image). */
  MediaEditOverlay?: (props: MediaEditOverlayProps) => ReactNode;
  /** Edit-mode Atlas bucket select and feedback button on sorting tray cards. */
  SortingTrayControls?: (props: SortingTrayControlsProps) => ReactNode;
  /**
   * Where a payload reports whether the learner has finished it, so the host can hold Next.
   *
   * Optional, and absence means "nobody is gating": a rail thumbnail and the editor canvas pass
   * none and behave as they always did. See `nextGate.tsx` — the host end is `useNextGateState`.
   */
  gate?: NextGate;
  /**
   * Seed for the shuffled card order on sorting slides (CEE-1214). See `itemOrder.ts`.
   *
   * On the seam rather than computed in the renderer because only the host can make it STICK: the
   * order must hold for as long as the learner is working the board and change for the next one, so
   * it has to live somewhere that outlives a mount. The SCORM runtime mints one per attempt and
   * keeps it in `cmi.suspend_data`; the editor mints one per Preview run, a Preview run being its
   * nearest thing to an attempt.
   *
   * Absent reads as zero, NOT as "do not shuffle" — whether to shuffle is decided by `mode`, and
   * every mode but `edit` shuffles. So a zip opened with no LMS at all, and a rail thumbnail, get a
   * shuffled board (the same one every time, the seed being fixed) rather than falling back to the
   * authored order that gives the answer key away.
   */
  itemOrderSeed?: number;
}

/** Preview/SCORM `Slot`: renders children and nothing else, so it tree-shakes to nothing. */
const identitySlot = ({ children }: SlotProps): ReactNode => children;

const defaultContext: RenderContextValue = {
  mode: 'preview',
  Slot: identitySlot,
  onTextChange: undefined,
  onTextCommit: undefined,
  resolveAsset: () => null,
  assetsPending: undefined,
  progress: {},
  onNext: undefined,
  nextLabel: undefined,
  strings: DEFAULT_CHROME_STRINGS,
  MediaEditOverlay: undefined,
  SortingTrayControls: undefined,
  gate: undefined,
  itemOrderSeed: undefined,
};

const RenderContext = createContext<RenderContextValue>(defaultContext);

export function useRenderContext(): RenderContextValue {
  return useContext(RenderContext);
}

/** True when a learner may answer. Edit-mode clicks edit content, they do not answer it. */
export function useAnswerable(): boolean {
  return useRenderContext().mode !== 'edit';
}

export interface RenderProviderProps extends Partial<Omit<RenderContextValue, 'mode'>> {
  mode: RenderMode;
  children: ReactNode;
}

export function RenderProvider({
  mode,
  Slot,
  onTextChange,
  onTextCommit,
  resolveAsset,
  assetsPending,
  progress,
  onNext,
  nextLabel,
  strings,
  MediaEditOverlay,
  SortingTrayControls,
  gate,
  itemOrderSeed,
  children,
}: RenderProviderProps) {
  const value = useMemo<RenderContextValue>(
    () => ({
      mode,
      Slot: Slot ?? identitySlot,
      onTextChange,
      onTextCommit,
      resolveAsset: resolveAsset ?? (() => null),
      assetsPending,
      progress: progress ?? {},
      onNext,
      nextLabel,
      strings: strings ?? DEFAULT_CHROME_STRINGS,
      MediaEditOverlay,
      SortingTrayControls,
      gate,
      itemOrderSeed,
    }),
    [
      mode,
      Slot,
      onTextChange,
      onTextCommit,
      resolveAsset,
      assetsPending,
      progress,
      onNext,
      nextLabel,
      strings,
      MediaEditOverlay,
      SortingTrayControls,
      gate,
      itemOrderSeed,
    ],
  );

  return <RenderContext.Provider value={value}>{children}</RenderContext.Provider>;
}

export { identitySlot };
